import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NavbarAr from "@/components/NavbarAr";
import FooterAr from "@/components/FooterAr";
import MediaGallery from "@/components/MediaGallery";
import CollapsibleSection from "@/components/CollapsibleSection";
import Brand from "@/components/Brand";
import BrandText from "@/components/BrandText";

// Events page — APPROVED & LOCKED (Feb 2026).
// Governing question: "How does CrewZNatioN come to life?"
//
// The page is composed as a continuous editorial journey:
//   Hero → Lead → §3 The Road So Far → §4 The Road Ahead.
//
// It documents a rhythm, not a calendar. Evidence precedes anticipation.
// No unverified significance is assigned to any documented gathering,
// and no unconfirmed date is published. See PRD §10.15 for the full
// final-lock record, and §10.14 (Sponsors) for the parallel review
// methodology.
//
// ─────────────────────────────────────────────────────────────────
// APPROVED COMPOSITION — do not modify without explicit
// re-authorization from the owner:
//
//   • §1 Editorial Hero (Option D — deliberate departure from the
//     text-over-image pattern used on Landing / About / Membership /
//     Sponsors). Sequence: overline → H1 → unobstructed photograph.
//   • Hero overline: "In Motion" — flat color #8f6b00, font-weight 800,
//     15px/16px, letter-spacing 0.24em. Sponsors overline treatment
//     reused verbatim for cross-page continuity.
//   • Hero H1: "Every gathering carries the journey forward."
//     font-display, 5xl/6xl, dark on ivory, left-aligned, max-w-3xl.
//   • Hero photograph: EVENTS_HERO_IMAGE, max-w-6xl container,
//     aspect-[5/3] (approx. 6.7% taller than 16:9 — cinematic
//     presence), kenburns motion. Tonal balance preserved via a
//     single CSS filter — contrast(1.08) brightness(0.94). NO
//     overlays, gradients, or vignettes; the photograph must not
//     be lightened or masked.
//   • §2 Lead paragraph: three sentences on plain ivory, placed
//     below the hero photograph. Locked verbatim.
//   • §3 The Road So Far — overline "Where we've been". Evidence-
//     first. Two accordion entries (Ishbilia + Blvd), factual meta
//     only. Descriptions are neutral factual single-line placeholders
//     to be replaced with genuine significance when the owner
//     supplies it. No dates until confirmed. Accordion mechanism
//     unchanged; CollapsibleSection variant="light" for ivory tone.
//   • §4 The Road Ahead — overline "Where we're going". Ongoing
//     rhythm as a continuous list, NOT a card grid. Five entries in
//     order: weekly meets → cars and coffee → group drives →
//     cultural collaborations → public gatherings. No "Coming Soon"
//     language anywhere.
//   • Continuous ivory tone from top to bottom. No gold horizon rule.
//
// Owner's non-blocking observation recorded for future consideration
// (NOT a lock condition): a ~8–12px reduction in the gap between the
// Hero H1 and the Hero photograph may be explored later if desired.
// ─────────────────────────────────────────────────────────────────

const EVENTS_HERO_IMAGE =
    "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/ks8wqytc_IMG_6139.JPG";

// Ishbilia — 3 videos + 32 photos
const ISHBILIA_MEDIA = [
    {
        type: "video",
        src: "/assets/events/ishbilia/ishbilia-video-1.mp4",
        thumbnail: "/assets/events/ishbilia/ishbilia-video-1-poster.jpg",
        caption: "CrewZNatioN at Ishbilia — Event Recap",
        ratio: "square",
        featured: true,
    },
    {
        type: "video",
        src: "/assets/events/ishbilia/ishbilia-video-2.mp4",
        thumbnail: "/assets/events/ishbilia/ishbilia-video-2-poster.jpg",
        caption: "Inside the meet — Ishbilia night",
        ratio: "square",
    },
    {
        type: "video",
        src: "/assets/events/ishbilia/ishbilia-video-3.mp4",
        thumbnail: "/assets/events/ishbilia/ishbilia-video-3-poster.jpg",
        caption: "Engines, lights, community",
        ratio: "square",
    },
    ...Array.from({ length: 32 }, (_, i) => ({
        type: "image",
        src: `/assets/events/ishbilia/ishbilia-${String(i + 1).padStart(2, "0")}.jpg`,
        caption: "Ishbilia Residential Compound",
        ratio: "square",
    })),
];

// Blvd — 25 photos
const BLVD_MEDIA = Array.from({ length: 25 }, (_, i) => ({
    type: "image",
    src: `/assets/events/blvd/blvd-${String(i + 1).padStart(2, "0")}.jpg`,
    caption: "Blvd — Prince Turki Alawal St.",
    ratio: "square",
}));

// ─────────────────────────────────────────────────────────────────
// Additional Road-So-Far entries. Each acknowledges the event on its
// own terms and hands the visitor forward to the Gallery. No
// internal production language surfaces to the visitor.
// Governed by PRD §31 (Event/Album Relationship).
// ─────────────────────────────────────────────────────────────────
const ALBUM_EVENTS = [
    {
        slug: "the-farm",
        testid: "event-the-farm",
        title: "The Farm",
        meta: "Saudi Arabia",
        description:
            "A CrewZNatioN gathering — the origin of the community's editorial record.",
    },
    {
        slug: "crewznation-x-rshield-cars-and-coffee",
        testid: "event-rshield-cars-coffee",
        title: "CrewZNatioN X RShield Cars & Coffee",
        meta: "Riyadh · Saudi Arabia",
        description:
            "A collaboration between CrewZNatioN and RShield that brought together automotive enthusiasts from across the community.",
    },
    {
        slug: "the-desert-end",
        testid: "event-the-desert-end",
        title: "The Desert End",
        meta: "Saudi Arabia",
        description:
            "A CrewZNatioN drive along a scenic Kingdom road, culminating where the paved road gives way to open desert.",
    },
];

const ROAD_AHEAD_ENTRIES = [
    {
        slug: "weekly-meets",
        title: "Weekly Meets.",
        note: "Regular meetups that keep members connected and the community moving.",
        titleAr: "اللقاءات الأسبوعية:",
        noteAr: "لقاءات منتظمة تجمع الأعضاء، وتحافظ على ترابط المجتمع واستمرار حركته.",
    },
    {
        slug: "cars-and-coffee",
        title: "Cars and Coffee.",
        note: "Relaxed morning meets at familiar places across Riyadh.",
        titleAr: "السيارات والقهوة:",
        noteAr: "لقاءات صباحية هادئة تجمع الأعضاء في أماكن اعتادوها، حيث يبدأ اليوم بالشغف والحديث قبل الانطلاق.",
    },
    {
        slug: "group-drives",
        title: "Group Drives.",
        note: "Shared drives shaped by the road, the destination, and the people travelling together.",
        titleAr: "الطلعات الجماعية:",
        noteAr: "رحلات يقودها الشغف، وتصنعها الطرق، وتجمعها الوجهة الواحدة.",
    },
    {
        slug: "cultural-collaborations",
        title: "Cultural Collaborations.",
        note: "Experiences created with organizations whose work connects naturally with the community.",
        titleAr: "التعاونات المجتمعية:",
        noteAr: "شراكات مع جهات تشاركنا القيم، وتسهم في إثراء تجربة المجتمع وتوسيع أثره.",
    },
    {
        slug: "public-gatherings",
        title: "Public Events.",
        note: "Open events that bring CrewZNatioN and the wider city together.",
        titleAr: "الفعاليات العامة:",
        noteAr: "فعاليات مفتوحة تجمع CrewZNatioN بالمجتمع، وتفتح المجال لمشاركة أوسع تحت مظلة واحدة.",
    },
];

export default function Events({ locale = "en", embedded = false }) {
    const Nav = locale === "ar" ? NavbarAr : Navbar;
    const PageFooter = locale === "ar" ? FooterAr : Footer;

    // Deep-link support: when the URL contains a hash matching one of the
    // documented event slugs (e.g. /events#the-farm, /ar/events#the-desert-end),
    // scroll to that entry after mount. Gallery.jsx uses these fragments to
    // navigate visitors from the Gallery titles directly to the matching
    // event on the Events page.
    const { hash } = useLocation();
    const activeHash = hash ? hash.slice(1) : "";
    useEffect(() => {
        if (!activeHash) return;
        // Wait one frame for CollapsibleSection default-open expansion to lay out.
        const t = setTimeout(() => {
            const el = document.getElementById(activeHash);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
        return () => clearTimeout(t);
    }, [activeHash]);

    return (
        <div
            dir={locale === "ar" ? "rtl" : "ltr"}
            id={embedded ? "events" : undefined}
            className={`${embedded ? "" : "min-h-screen "}bg-[var(--crewz-ivory)] text-[var(--crewz-text-dark)]`}
            data-testid="events-page"
        >
            {!embedded && <Nav />}

            {/* ============================================================
                §1 EDITORIAL HERO — Option D composition.
                Typography sits ABOVE the photograph on ivory. The image
                below spans the content width as an unobstructed
                photograph. This is a deliberate departure from the
                text-over-image pattern used on Landing / About /
                Membership / Sponsors — it gives the Events page its
                own editorial identity and lets the photograph carry
                its authenticity uncompromised.
                ============================================================ */}
            <section
                className="pt-16 md:pt-24"
                data-testid="events-hero"
            >
                {/* Typography block */}
                <div className="px-6 md:px-12 lg:px-20 max-w-5xl mx-auto">
                    {locale === "ar" ? (
                        <p className="overline overline-primary mb-6 text-[var(--crewz-gold)]">
                            المسيرة مستمرة
                        </p>
                    ) : (
                        <p className="overline mb-6 text-[#8f6b00] !font-extrabold !text-[24px] md:!text-[26px] !leading-[1.35] !tracking-[0.24em]">
                            In Motion
                        </p>
                    )}
                    <h1
                        className="font-display text-5xl md:text-6xl text-[var(--crewz-text-dark)] leading-[0.98] max-w-3xl"
                        data-testid="events-title"
                        style={
                            locale === "ar"
                                ? {
                                      fontFamily:
                                          '"Alexandria", "IBM Plex Sans Arabic", system-ui, sans-serif',
                                      fontWeight: 700,
                                      lineHeight: 1.22,
                                      letterSpacing: "-0.01em",
                                  }
                                : undefined
                        }
                    >
                        {locale === "ar" ? (
                            <>
                                كل فعالية تدفع
                                <br />
                                المسيرة إلى الأمام.
                            </>
                        ) : (
                            "Every Event Carries the Journey Forward."
                        )}
                    </h1>
                </div>

                {/* Photograph — unobstructed, content-width. Tonal balance
                    preserved via subtle CSS contrast/brightness only — no
                    overlays, gradients, or vignettes. Aspect 5:3 (approx.
                    6.7% taller than 16:9) for stronger cinematic presence. */}
                <div className="mt-14 md:mt-20 px-6 md:px-12 lg:px-20 max-w-6xl mx-auto">
                    <figure
                        className="relative w-full aspect-[5/3] overflow-hidden bg-[var(--crewz-stone)]"
                        data-testid="events-hero-image"
                    >
                        <img
                            src={EVENTS_HERO_IMAGE}
                            alt=""
                            role="presentation"
                            className="w-full h-full object-cover kenburns"
                            style={{ filter: "contrast(1.08) brightness(0.94)" }}
                        />
                    </figure>
                </div>
            </section>

            {/* ============================================================
                §2 LEAD — Editorial standoff below the hero photograph,
                on plain ivory. Locked verbatim.
                ============================================================ */}
            <section
                className="px-6 md:px-12 lg:px-20 pt-14 md:pt-20 max-w-5xl mx-auto"
                data-testid="events-lead"
            >
                {locale === "ar" ? (
                    <p className="font-body font-light text-[var(--crewz-text-dark)]/85 max-w-3xl mx-auto text-center !text-[22px] md:!text-[26px] !leading-[1.6]">
                        تنبض <Brand /> بالحياة من خلال اللحظات التي تجمع أفرادها. فكل فعالية تضيف قصة جديدة، وتقرب الأشخاص من بعضهم، وتمنح المجتمع فصلاً جديدًا في رحلته المستمرة. وهذه مسيرة <Brand /> حتى اليوم… أما القادم، فسيكون أجمل.
                    </p>
                ) : (
                    <p className="font-body text-lg md:text-xl text-[var(--crewz-text-dark)]/85 leading-relaxed font-light max-w-3xl">
                        <Brand /> comes to life through the moments we share.
                        Every gathering brings people, machines, and stories
                        together, adding another chapter to the community we
                        continue to build. This is the road so far — and the road
                        ahead.
                    </p>
                )}
            </section>

            {/* ============================================================
                §3 THE ROAD SO FAR — Evidence-first. Two documented
                gatherings, factual meta only, neutral factual interim
                descriptions until owner-supplied significance replaces
                them. Accordion mechanism preserved.
                ============================================================ */}
            <section
                className="px-6 md:px-12 lg:px-20 pt-24 md:pt-32 max-w-6xl mx-auto"
                data-testid="events-road-so-far"
            >
                <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                    {locale === "ar" ? "لحظات صنعت المسيرة" : "Where we've been"}
                </p>
                {locale === "ar" && (
                    <p className="font-body text-sm md:text-base tracking-[0.22em] uppercase font-bold text-[var(--crewz-text-dark)]/70 mb-4">
                        فعاليات جماهيرية
                    </p>
                )}
                <h2
                    className="font-display text-4xl md:text-5xl text-[var(--crewz-text-dark)] leading-[0.98]"
                    data-testid="events-road-so-far-title"
                >
                    {locale === "ar" ? "صُممت لتقدم تجربة استثنائية." : "The road so far."}
                </h2>
                <p className={`font-body text-base md:text-lg text-[var(--crewz-text-dark)]/70 max-w-2xl font-light leading-relaxed mt-6${locale === "ar" ? " !text-[18px] md:!text-[20px]" : ""}`}>
                    {locale === "ar"
                        ? "نصمم فعالياتنا وفق أعلى معايير التنظيم والسلامة، لنقدم تجربة متكاملة للمشاركين، والشركاء، والزوار. ونسهم في ترسيخ ثقافة السيارات والدراجات النارية، والإسهام في نموها وتطورها."
                        : "Open any entry to see the community's own record of it."}
                </p>

                <div className="mt-14 md:mt-16">
                    <CollapsibleSection
                        testid="event-ishbilia"
                        variant="light"
                        title="Ishbilia Residential Compound"
                        meta="Riyadh · Saudi Arabia · 35 items"
                        description="A community gathering held inside the Ishbilia Residential Compound in Riyadh."
                    >
                        <MediaGallery items={ISHBILIA_MEDIA} idPrefix="ishbilia" />
                    </CollapsibleSection>

                    <CollapsibleSection
                        testid="event-blvd"
                        variant="light"
                        title="Blvd — Prince Turki Alawal St."
                        meta="Riyadh · Saudi Arabia · 25 photos"
                        description="A community gathering held along Prince Turki Alawal Street in Riyadh."
                    >
                        <MediaGallery items={BLVD_MEDIA} idPrefix="blvd" />
                    </CollapsibleSection>

                    {ALBUM_EVENTS.map((entry) => (
                        <CollapsibleSection
                            key={entry.slug}
                            id={entry.slug}
                            testid={entry.testid}
                            variant="light"
                            title={entry.title}
                            meta={entry.meta}
                            description={entry.description}
                            defaultOpen={activeHash === entry.slug}
                        >
                            <div
                                className="max-w-3xl"
                                data-testid={`${entry.testid}-continuation`}
                            >
                                <Link
                                    to="/gallery"
                                    className="inline-flex items-center gap-2 font-body text-xs md:text-sm tracking-[0.22em] uppercase font-bold text-[var(--crewz-gold)] hover:opacity-80 transition-opacity"
                                    data-testid={`${entry.testid}-gallery-link`}
                                >
                                    The Gallery →
                                </Link>
                            </div>
                        </CollapsibleSection>
                    ))}
                </div>
            </section>

            {/* ============================================================
                §4 THE ROAD AHEAD — Rhythm as ongoing practice. Editorial
                paragraph + compact left-aligned list. No card grid, no
                "Coming Soon" language, no dates.
                ============================================================ */}
            <section
                className="px-6 md:px-12 lg:px-20 pt-20 md:pt-28 pb-24 md:pb-32 max-w-5xl mx-auto"
                data-testid="events-road-ahead"
            >
                <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                    {locale === "ar" ? "لقاءات المجتمع" : "Where we're going"}
                </p>
                {locale === "ar" && (
                    <p className="font-body text-sm md:text-base tracking-[0.22em] uppercase font-bold text-[var(--crewz-text-dark)]/70 mb-4">
                        صُممت لأعضاء المجتمع.
                    </p>
                )}
                <h2
                    className="font-display text-4xl md:text-5xl text-[var(--crewz-text-dark)] leading-[0.98]"
                    data-testid="events-road-ahead-title"
                >
                    {locale === "ar" ? "حيث يجتمع الشغف." : "The road ahead."}
                </h2>
                {locale === "ar" ? (
                    <>
                        <p className="font-body text-lg md:text-xl text-[var(--crewz-text-dark)]/85 leading-relaxed font-light max-w-3xl mt-8 !text-[20px] md:!text-[22px]">
                            لقاءاتنا ليست مجرد تجمعات، بل مساحة يلتقي فيها الأعضاء، لتبادل الخبرات، والتعبير عن شغفهم، واستلهام أفكار جديدة.
                        </p>
                        <p className="font-body text-lg md:text-xl text-[var(--crewz-text-dark)]/85 leading-relaxed font-light max-w-3xl mt-4 !text-[20px] md:!text-[22px]">
                            وهكذا تستمر الروابط في النمو، ويبقى المجتمع أكثر قربًا وترابطًا.
                        </p>
                    </>
                ) : (
                    <p className="font-body text-lg md:text-xl text-[var(--crewz-text-dark)]/85 leading-relaxed font-light max-w-3xl mt-8">
                        The road ahead is shaped by a rhythm the community already
                        knows — weekly meets, cars and coffee mornings, group
                        drives, cultural collaborations, and public events.
                        Dates are shared with members once they are confirmed, but
                        the movement itself continues.
                    </p>
                )}

                <ul className="mt-12 md:mt-14 space-y-8 max-w-3xl">
                    {ROAD_AHEAD_ENTRIES.map((entry) => (
                        <li
                            key={entry.slug}
                            data-testid={`road-ahead-${entry.slug}`}
                            className="font-body text-base md:text-lg leading-relaxed"
                        >
                            {locale === "ar" ? (
                                <div className="flex items-baseline gap-2">
                                    <span className="font-semibold text-[var(--crewz-text-dark)] flex-shrink-0 whitespace-nowrap">
                                        {entry.titleAr}
                                    </span>
                                    <span className="text-[var(--crewz-text-dark)]/75 font-light flex-1">
                                        <BrandText>{entry.noteAr}</BrandText>
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <span className="font-semibold text-[var(--crewz-text-dark)]">
                                        {entry.title}
                                    </span>{" "}
                                    <span className="text-[var(--crewz-text-dark)]/75 font-light">
                                        <BrandText>{entry.note}</BrandText>
                                    </span>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            </section>

            {locale === "ar" && (
                <section
                    className="px-6 md:px-12 lg:px-20 pt-20 md:pt-28 pb-24 md:pb-32 max-w-5xl mx-auto"
                    data-testid="events-upcoming-ar"
                >
                    <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                        ما القادم؟
                    </p>
                    <p className="font-body text-sm md:text-base tracking-[0.22em] uppercase font-bold text-[var(--crewz-text-dark)]/70 mb-4">
                        الفعاليات القادمة
                    </p>
                    <h2 className="font-display text-4xl md:text-5xl text-[var(--crewz-text-dark)] leading-[0.98]">
                        لكل إعلان وقته.
                    </h2>
                    <p className="font-body text-lg md:text-xl text-[var(--crewz-text-dark)]/85 leading-relaxed font-light max-w-3xl mt-8 !text-[20px] md:!text-[22px]">
                        لا نعلن عن أي فعالية إلا بعد اكتمال جميع تفاصيلها. وعندما نشارككم موعدًا أو موقعًا أو آلية للمشاركة، فذلك يعني أن كل ما ترونه قد أصبح مؤكدًا وجاهزًا.
                    </p>
                    <div className="mt-12">
                        <span
                            data-testid="events-upcoming-cta-ar"
                            className="inline-flex items-center gap-2 font-body text-sm tracking-[0.22em] uppercase font-bold text-[var(--crewz-gold)]/60 cursor-default"
                        >
                            ترقب القادم من <Brand />
                        </span>
                    </div>
                </section>
            )}

            {!embedded && <PageFooter />}
        </div>
    );
}
